import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useBeforeUnload,
} from "react-router-dom";
import Alert from "../../components/common/Alert";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Input from "../../components/common/Input";
import PageHeader from "../../components/common/PageHeader";
import Select from "../../components/common/Select";
import Table from "../../components/common/Table";
import ActionButton from "../../components/common/ActionButton";
import Checkbox from "../../components/common/Checkbox";
import Modal from "../../components/common/Modal";
import { useStore } from "../../context/StoreContext";
import {
  useGetCategoriesQuery,
  useGetItemsQuery,
  useGetSlotsQuery,
  useLazyGetSlotDaysQuery,
  useCreateSlotMutation,
  useUpdateSlotMutation,
  useDeleteSlotMutation,
  useReplaceSlotDaysMutation,
  useGetItemSlotDaysQuery,
  useLazyGetItemSlotDaysQuery,
  useLazyGetItemSlotDaysBySlotQuery,
  useReplaceItemSlotDaysMutation,
} from "../../store/api";

const DAYS = [
  { dayOfWeek: 0, label: "Sunday" },
  { dayOfWeek: 1, label: "Monday" },
  { dayOfWeek: 2, label: "Tuesday" },
  { dayOfWeek: 3, label: "Wednesday" },
  { dayOfWeek: 4, label: "Thursday" },
  { dayOfWeek: 5, label: "Friday" },
  { dayOfWeek: 6, label: "Saturday" },
];
const getTodayDayValue = () => String(new Date().getDay());

const getItemId = (item) => String(item.itemId || item.id || "");
const getItemName = (item) => item.name || item.itemName || "Unnamed Item";
const getCategoryId = (item) => String(item.categoryId || "uncategorized");
const normalizeSlotName = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return null;
  return trimmed.slice(0, 64);
};
const toMinutes = (timeValue) => {
  if (!/^\d{2}:\d{2}(:\d{2})?$/.test(timeValue || "")) return Number.NaN;
  const [hours, minutes] = timeValue.split(":").map(Number);
  return hours * 60 + minutes;
};
const asSeconds = (value) => {
  if (!value) return "";
  if (/^\d{2}:\d{2}:\d{2}$/.test(value)) return value;
  if (/^\d{2}:\d{2}$/.test(value)) return `${value}:00`;
  return value;
};
const getAssignmentKey = (assignment) =>
  `${assignment?.slotId || ""}-${Number(assignment?.dayOfWeek)}`;
const normalizeAssignments = (rows) => {
  if (!Array.isArray(rows)) return [];
  const unique = new Map();
  rows.forEach((row) => {
    const slotId = row?.slotId;
    const dayOfWeek = Number(row?.dayOfWeek);
    if (!slotId || Number.isNaN(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6) return;
    unique.set(`${slotId}-${dayOfWeek}`, { slotId, dayOfWeek });
  });
  return Array.from(unique.values());
};
const hasAssignment = (assignments, target) =>
  (assignments || []).some(
    (entry) =>
      entry.slotId === target.slotId &&
      Number(entry.dayOfWeek) === Number(target.dayOfWeek),
  );

const mapSchedulingError = (error, fallbackMessage) => {
  const detail = error?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }

  const code = String(
    error?.data?.code || error?.data?.errorCode || error?.data?.title || "",
  )
    .trim()
    .toUpperCase();

  const mapped = {
    SLOT_REQUIRED: "Slot is required.",
    SLOT_NAME_REQUIRED: "Slot name is required.",
    SLOT_NAME_INVALID: "Slot name is invalid.",
    SLOT_TIME_INVALID: "Slot time is invalid. Start time must be earlier than end time.",
    SLOT_DAY_PAYLOAD_INVALID: "Invalid slot day payload.",
    SLOT_OVERLAP: "Selected days conflict with an existing slot time range.",
    ITEM_SLOT_DAY_PAYLOAD_INVALID: "Invalid item slot-day payload.",
    ITEM_SLOT_DAY_INVALID: "Invalid item-slot day mapping.",
    ITEM_SLOT_INVALID: "Invalid slot selection for item assignment.",
    ITEM_NOT_FOUND: "Item not found. Please refresh and try again.",
    STORE_NOT_FOUND: "Store not found. Please reselect store and try again.",
    SLOT_DAY_INVALID: "Selected day is not valid for this slot.",
    SLOT_NOT_FOUND: "Selected slot was not found. Please refresh and try again.",
  };

  return mapped[code] || error?.data?.detail || fallbackMessage;
};

const getApiErrorDedupKey = (error, fallbackMessage) => {
  const title = String(error?.data?.title || error?.data?.code || "").trim();
  const detail = String(error?.data?.detail || mapSchedulingError(error, fallbackMessage) || "").trim();
  if (!title && !detail) return "";
  return `${title}|${detail}`.toLowerCase();
};

const WeeklyScheduling = () => {
  const { selectedStoreId } = useStore();
  const { data: itemsData, isLoading: itemsLoading } = useGetItemsQuery(
    selectedStoreId,
    { skip: !selectedStoreId },
  );
  const { data: categoriesData } = useGetCategoriesQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });
  const { data: slotsData, refetch: refetchSlots } = useGetSlotsQuery(
    { storeId: selectedStoreId },
    { skip: !selectedStoreId },
  );

  const items = useMemo(() => (Array.isArray(itemsData) ? itemsData : []), [itemsData]);
  const categories = useMemo(
    () => (Array.isArray(categoriesData) ? categoriesData : []),
    [categoriesData],
  );
  const slots = useMemo(() => (Array.isArray(slotsData) ? slotsData : []), [slotsData]);

  const [activeItemId, setActiveItemId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [daySelectionMode, setDaySelectionMode] = useState("single");
  const [selectedDay, setSelectedDay] = useState(() => getTodayDayValue());
  const [selectedDaysMulti, setSelectedDaysMulti] = useState(() => [
    getTodayDayValue(),
  ]);
  const [reportDay, setReportDay] = useState(() => getTodayDayValue());
  const reportDayNumber = Number(reportDay);
  const [dayDropdownOpen, setDayDropdownOpen] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [slotDaysBySlotId, setSlotDaysBySlotId] = useState({});
  const [itemAssignmentsByItem, setItemAssignmentsByItem] = useState({});
  const [savedAssignmentsByItem, setSavedAssignmentsByItem] = useState({});
  const [dirtyItemIds, setDirtyItemIds] = useState([]);
  const [lastAssignmentsSyncAt, setLastAssignmentsSyncAt] = useState(null);
  const [dayAssignmentsBySlot, setDayAssignmentsBySlot] = useState({});
  const [dayAssignmentsLoading, setDayAssignmentsLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [toasts, setToasts] = useState([]);
  const [saveProgressModalOpen, setSaveProgressModalOpen] = useState(false);
  const [saveProgressSummary, setSaveProgressSummary] = useState({
    total: 0,
    completed: 0,
    success: 0,
    failed: 0,
  });
  const [createSlotModalOpen, setCreateSlotModalOpen] = useState(false);
  const [editSlotModalOpen, setEditSlotModalOpen] = useState(false);
  const [slotForm, setSlotForm] = useState({ slotName: "", startTime: "", endTime: "" });
  const dayDropdownRef = useRef(null);
  const lastToastByKeyRef = useRef(new Map());

  const { data: activeItemSlotDaysData } = useGetItemSlotDaysQuery(
    { storeId: selectedStoreId, itemId: activeItemId },
    { skip: !selectedStoreId || !activeItemId },
  );
  const [createSlot, createSlotState] = useCreateSlotMutation();
  const [updateSlot, updateSlotState] = useUpdateSlotMutation();
  const [deleteSlot, deleteSlotState] = useDeleteSlotMutation();
  const [replaceSlotDays, replaceSlotDaysState] = useReplaceSlotDaysMutation();
  const [fetchSlotDays] = useLazyGetSlotDaysQuery();
  const [fetchItemSlotDays] = useLazyGetItemSlotDaysQuery();
  const [fetchItemSlotDaysBySlot] = useLazyGetItemSlotDaysBySlotQuery();
  const [replaceItemSlotDays, replaceItemSlotsState] = useReplaceItemSlotDaysMutation();

  const refreshAllItemSlotAssignments = useCallback(async () => {
    if (!selectedStoreId || items.length === 0) {
      setItemAssignmentsByItem({});
      setSavedAssignmentsByItem({});
      return;
    }

    const next = {};
    for (const item of items) {
      const itemId = getItemId(item);
      if (!itemId) continue;
      try {
        // eslint-disable-next-line no-await-in-loop
        const rows = await fetchItemSlotDays({ storeId: selectedStoreId, itemId }).unwrap();
        next[itemId] = normalizeAssignments(rows);
      } catch (error) {
        next[itemId] = [];
      }
    }

    setItemAssignmentsByItem(next);
    setSavedAssignmentsByItem(next);
    setLastAssignmentsSyncAt(Date.now());
  }, [selectedStoreId, items, fetchItemSlotDays]);

  useEffect(() => {
    if (!activeItemId || dirtyItemIds.includes(activeItemId)) return;
    const backendAssignments = normalizeAssignments(activeItemSlotDaysData);
    setSavedAssignmentsByItem((prev) => ({
      ...prev,
      [activeItemId]: backendAssignments,
    }));
    setItemAssignmentsByItem((prev) => ({
      ...prev,
      [activeItemId]: backendAssignments,
    }));
    setLastAssignmentsSyncAt(Date.now());
  }, [activeItemId, activeItemSlotDaysData, dirtyItemIds]);

  useEffect(() => {
    const loadAllItemSlots = async () => {
      if (!selectedStoreId || items.length === 0) return;
      await refreshAllItemSlotAssignments();
    };
    loadAllItemSlots();
  }, [selectedStoreId, items.length, refreshAllItemSlotAssignments]);

  useEffect(() => {
    let cancelled = false;
    const loadDays = async () => {
      if (!selectedStoreId || slots.length === 0) {
        setSlotDaysBySlotId({});
        return;
      }
      const next = {};
      for (const slot of slots) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const rows = await fetchSlotDays({
            storeId: selectedStoreId,
            slotId: slot.slotId,
          }).unwrap();
          next[slot.slotId] = Array.isArray(rows)
            ? [...new Set(rows.map((row) => Number(row.dayOfWeek)).filter((d) => d >= 0 && d <= 6))]
            : [];
        } catch (error) {
          next[slot.slotId] = [];
        }
      }
      if (!cancelled) setSlotDaysBySlotId(next);
    };
    loadDays();
    return () => {
      cancelled = true;
    };
  }, [selectedStoreId, slots, fetchSlotDays]);

  const reportDaySlots = useMemo(
    () =>
      slots
        .filter((slot) =>
          (slotDaysBySlotId[slot.slotId] || [])
            .map(Number)
            .includes(reportDayNumber),
        )
        .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime)),
    [slots, slotDaysBySlotId, reportDayNumber],
  );

  useEffect(() => {
    let cancelled = false;
    const loadDayAssignments = async () => {
      if (!selectedStoreId || Number.isNaN(reportDayNumber)) {
        setDayAssignmentsBySlot({});
        return;
      }
      if (reportDaySlots.length === 0) {
        setDayAssignmentsBySlot({});
        return;
      }

      setDayAssignmentsLoading(true);
      const next = {};
      for (const slot of reportDaySlots) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const rows = await fetchItemSlotDaysBySlot({
            storeId: selectedStoreId,
            slotId: slot.slotId,
            dayOfWeek: reportDayNumber,
          }).unwrap();
          next[slot.slotId] = Array.isArray(rows) ? rows : [];
        } catch (error) {
          next[slot.slotId] = [];
        }
      }
      if (!cancelled) {
        setDayAssignmentsBySlot(next);
        setDayAssignmentsLoading(false);
      }
    };

    loadDayAssignments();
    return () => {
      cancelled = true;
    };
  }, [selectedStoreId, reportDayNumber, reportDaySlots, fetchItemSlotDaysBySlot]);

  const pushToast = (tone, message, options = {}) => {
    if (!message) return;
    const normalizedMessageKey = `${tone}|${String(message).trim().toLowerCase()}`;
    const dedupeKey = options?.dedupeKey || "";
    const dedupeWindowMs =
      typeof options?.dedupeWindowMs === "number" ? options.dedupeWindowMs : 2500;
    const now = Date.now();

    const lastShownForMessage = lastToastByKeyRef.current.get(normalizedMessageKey) || 0;
    if (now - lastShownForMessage < dedupeWindowMs) return;
    lastToastByKeyRef.current.set(normalizedMessageKey, now);

    if (dedupeKey) {
      const lastShownAt = lastToastByKeyRef.current.get(dedupeKey) || 0;
      if (now - lastShownAt < dedupeWindowMs) return;
      lastToastByKeyRef.current.set(dedupeKey, now);
    }
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, tone, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 4000);
  };

  useEffect(() => {
    if (!validationError) return;
    pushToast("error", validationError, {
      dedupeKey: `validation|${String(validationError).trim().toLowerCase()}`,
    });
    setValidationError("");
  }, [validationError]);

  useEffect(() => {
    if (!successMessage) return;
    pushToast("success", successMessage);
    setSuccessMessage("");
  }, [successMessage]);

  useEffect(() => {
    if (!createSlotState.isError) return;
    const message = mapSchedulingError(createSlotState.error, "Failed to create slot.");
    pushToast(
      "error",
      message,
      { dedupeKey: getApiErrorDedupKey(createSlotState.error, message) },
    );
  }, [createSlotState.isError, createSlotState.error]);

  useEffect(() => {
    if (!updateSlotState.isError) return;
    const message = mapSchedulingError(updateSlotState.error, "Failed to update slot.");
    pushToast(
      "error",
      message,
      { dedupeKey: getApiErrorDedupKey(updateSlotState.error, message) },
    );
  }, [updateSlotState.isError, updateSlotState.error]);

  useEffect(() => {
    if (!replaceSlotDaysState.isError) return;
    const message = mapSchedulingError(
      replaceSlotDaysState.error,
      "Failed to update slot day mappings.",
    );
    pushToast(
      "error",
      message,
      { dedupeKey: getApiErrorDedupKey(replaceSlotDaysState.error, message) },
    );
  }, [replaceSlotDaysState.isError, replaceSlotDaysState.error]);

  useEffect(() => {
    if (!replaceItemSlotsState.isError) return;
    const message = mapSchedulingError(
      replaceItemSlotsState.error,
      "Failed to save item slot assignments.",
    );
    pushToast(
      "error",
      message,
      { dedupeKey: getApiErrorDedupKey(replaceItemSlotsState.error, message) },
    );
  }, [replaceItemSlotsState.isError, replaceItemSlotsState.error]);

  useEffect(() => {
    const onOutside = (event) => {
      if (dayDropdownRef.current && !dayDropdownRef.current.contains(event.target)) {
        setDayDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const categoryNameById = useMemo(() => {
    const map = { uncategorized: "Uncategorized" };
    categories.forEach((category) => {
      const id = String(category.categoryId || category.id || "");
      if (id) map[id] = category.name || category.categoryName || "Unnamed Category";
    });
    return map;
  }, [categories]);

  const filteredItems = useMemo(() => {
    const query = itemSearch.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => {
      const name = getItemName(item).toLowerCase();
      const category = (categoryNameById[getCategoryId(item)] || "").toLowerCase();
      return name.includes(query) || category.includes(query);
    });
  }, [items, itemSearch, categoryNameById]);

  const groupedItems = useMemo(() => {
    const grouped = {};
    filteredItems.forEach((item) => {
      const categoryId = getCategoryId(item);
      if (!grouped[categoryId]) {
        grouped[categoryId] = {
          categoryId,
          categoryName: categoryNameById[categoryId] || "Uncategorized",
          items: [],
        };
      }
      grouped[categoryId].items.push(item);
    });
    return Object.values(grouped).sort((a, b) => a.categoryName.localeCompare(b.categoryName));
  }, [filteredItems, categoryNameById]);

  useEffect(() => {
    if (!groupedItems.length) {
      setSelectedCategoryId("");
      return;
    }
    if (!groupedItems.some((group) => group.categoryId === selectedCategoryId)) {
      setSelectedCategoryId(groupedItems[0].categoryId);
    }
  }, [groupedItems, selectedCategoryId]);

  const selectedDayNumber = Number(selectedDay);
  const slotOptions = useMemo(
    () =>
      slots
        .map((slot) => ({
          slotId: slot.slotId,
          slotName: normalizeSlotName(slot.slotName),
          startTime: slot.startTime,
          endTime: slot.endTime,
        }))
        .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime)),
    [slots],
  );
  const selectedSlot = slotOptions.find((slot) => slot.slotId === selectedSlotId) || null;
  const selectedSlotAllowedDays = useMemo(
    () =>
      new Set(
        (slotDaysBySlotId[selectedSlotId] || [])
          .map(Number)
          .filter((dayOfWeek) => dayOfWeek >= 0 && dayOfWeek <= 6),
      ),
    [selectedSlotId, slotDaysBySlotId],
  );

  const selectedDayLabel = useMemo(() => {
    const selected = DAYS.filter((day) => selectedDaysMulti.includes(String(day.dayOfWeek)));
    if (!selected.length) return "Select day";
    if (selected.length === 1) return selected[0].label;
    return `${selected[0].label} +${selected.length - 1}`;
  }, [selectedDaysMulti]);

  const selectedDayNamesText = useMemo(
    () =>
      DAYS.filter((day) => selectedDaysMulti.includes(String(day.dayOfWeek)))
        .map((day) => day.label)
        .join(", "),
    [selectedDaysMulti],
  );

  const selectedCategoryItems =
    groupedItems.find((group) => group.categoryId === selectedCategoryId)?.items || [];
  const allCategoryItemsSelected =
    selectedCategoryItems.length > 0 &&
    selectedCategoryItems.every((item) => selectedItemIds.includes(getItemId(item)));
  const validSlotIdSet = useMemo(
    () => new Set(slotOptions.map((slot) => slot.slotId).filter(Boolean)),
    [slotOptions],
  );

  const markDirty = (itemId) => {
    setDirtyItemIds((prev) => (prev.includes(itemId) ? prev : [...prev, itemId]));
  };

  const resolveTargetAssignments = () => {
    if (!selectedSlotId) return [];
    const daysSource = daySelectionMode === "single" ? [selectedDay] : selectedDaysMulti;
    return daysSource
      .map(Number)
      .filter((dayOfWeek) => dayOfWeek >= 0 && dayOfWeek <= 6)
      .map((dayOfWeek) => ({ slotId: selectedSlotId, dayOfWeek }));
  };

  const assignItemsToSelection = async (itemIds) => {
    const uniqueIds = [...new Set((itemIds || []).filter(Boolean))];
    if (!uniqueIds.length) return;
    if (!selectedSlotId) {
      setValidationError("Please select a slot first.");
      return;
    }
    const targetAssignments = resolveTargetAssignments();
    if (!targetAssignments.length) {
      setValidationError("Please select at least one day.");
      return;
    }
    const requestedDays = [...new Set(targetAssignments.map((entry) => Number(entry.dayOfWeek)))];
    const currentDays = (slotDaysBySlotId[selectedSlotId] || [])
      .map(Number)
      .filter((dayOfWeek) => dayOfWeek >= 0 && dayOfWeek <= 6);
    const mergedDays = [...new Set([...currentDays, ...requestedDays])];

    if (mergedDays.length !== currentDays.length) {
      try {
        await replaceSlotDays({
          storeId: selectedStoreId,
          slotId: selectedSlotId,
          dayOfWeeks: mergedDays,
        }).unwrap();
        setSlotDaysBySlotId((prev) => ({ ...prev, [selectedSlotId]: mergedDays }));
      } catch (error) {
        setValidationError(mapSchedulingError(error, "Failed to update slot day mappings."));
        return;
      }
    }

    const nextAssignmentsByItem = { ...itemAssignmentsByItem };
    let addedCount = 0;
    const duplicateDays = [];

    uniqueIds.forEach((itemId) => {
      const current = nextAssignmentsByItem[itemId] || [];
      const merged = [...current];
      targetAssignments.forEach((assignment) => {
        if (!hasAssignment(merged, assignment)) {
          merged.push(assignment);
          addedCount += 1;
        } else {
          duplicateDays.push(Number(assignment.dayOfWeek));
        }
      });
      nextAssignmentsByItem[itemId] = merged;
    });

    if (addedCount > 0) {
      setItemAssignmentsByItem(nextAssignmentsByItem);
    }

    const duplicateDayNames = [...new Set(duplicateDays)]
      .map((dayOfWeek) => DAYS.find((day) => day.dayOfWeek === dayOfWeek)?.label)
      .filter(Boolean);
    const requestedDayNames = [...new Set(targetAssignments.map((entry) => Number(entry.dayOfWeek)))]
      .map((dayOfWeek) => DAYS.find((day) => day.dayOfWeek === dayOfWeek)?.label)
      .filter(Boolean);
    const addedDayNames = requestedDayNames.filter((label) => !duplicateDayNames.includes(label));

    if (addedCount === 0) {
      setValidationError(
        duplicateDayNames.length > 0
          ? `Already assigned for selected day(s): ${duplicateDayNames.join(", ")}.`
          : "Already assigned for selected day(s).",
      );
      return;
    }
    uniqueIds.forEach((itemId) => markDirty(itemId));
    setValidationError("");
    if (duplicateDayNames.length > 0) {
      setSuccessMessage(
        `Added for: ${addedDayNames.join(", ")}. Already assigned: ${duplicateDayNames.join(", ")}.`,
      );
    } else {
      setSuccessMessage(`Added for: ${requestedDayNames.join(", ")}.`);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    assignItemsToSelection([event.dataTransfer.getData("text/plain")]);
  };
  const handleDragOver = (event) => event.preventDefault();

  const handleCreateSlot = async () => {
    if (!selectedStoreId) return;
    const slotName = normalizeSlotName(slotForm.slotName);
    const startTime = asSeconds(slotForm.startTime);
    const endTime = asSeconds(slotForm.endTime);
    const requestedDays = selectedDaysMulti
      .map(Number)
      .filter((d) => d >= 0 && d <= 6);
    if (!slotName) {
      setValidationError("Slot name is required.");
      return;
    }
    if (!startTime || !endTime || toMinutes(startTime) >= toMinutes(endTime)) {
      setValidationError("Slot time is invalid. Start time must be earlier than end time.");
      return;
    }

    // Reuse an existing global slot definition when name/time matches,
    // then update day mappings instead of creating duplicates.
    const existingSlot = slots.find(
      (slot) =>
        normalizeSlotName(slot.slotName) === slotName &&
        slot.startTime === startTime &&
        slot.endTime === endTime,
    );

    try {
      let targetSlotId = existingSlot?.slotId || "";
      if (!targetSlotId) {
        const created = await createSlot({
          storeId: selectedStoreId,
          payload: { slotName, startTime, endTime, isActive: true },
        }).unwrap();
        targetSlotId = created?.slotId || "";
      }

      // Backend auto-maps brand new slots to all days.
      // Update day applicability only when user explicitly selects a subset.
      const isExplicitApplicabilityChange =
        requestedDays.length > 0 && requestedDays.length < DAYS.length;
      if (isExplicitApplicabilityChange || existingSlot) {
        const currentDays = slotDaysBySlotId[targetSlotId] || [];
        const mergedDays = existingSlot
          ? [...new Set([...currentDays, ...requestedDays])]
          : requestedDays;
        await replaceSlotDays({
          storeId: selectedStoreId,
          slotId: targetSlotId,
          dayOfWeeks: mergedDays,
        }).unwrap();
      }

      await refetchSlots();
      setSelectedSlotId(targetSlotId);
      setCreateSlotModalOpen(false);
      setSuccessMessage("Slot created.");
    } catch (error) {
      setValidationError(mapSchedulingError(error, "Failed to create slot."));
    }
  };

  const openEditSlot = () => {
    if (!selectedSlot) {
      setValidationError("Please select a slot to edit.");
      return;
    }
    setSlotForm({
      slotName: normalizeSlotName(selectedSlot.slotName) || "",
      startTime: selectedSlot.startTime?.slice(0, 5) || "",
      endTime: selectedSlot.endTime?.slice(0, 5) || "",
    });
    setSelectedDaysMulti(
      (slotDaysBySlotId[selectedSlot.slotId] || [selectedDayNumber]).map((d) =>
        String(d),
      ),
    );
    setEditSlotModalOpen(true);
  };

  const handleEditSlot = async () => {
    if (!selectedStoreId || !selectedSlot) return;
    const slotName = normalizeSlotName(slotForm.slotName);
    const startTime = asSeconds(slotForm.startTime);
    const endTime = asSeconds(slotForm.endTime);
    if (!slotName) {
      setValidationError("Slot name is required.");
      return;
    }
    if (!startTime || !endTime || toMinutes(startTime) >= toMinutes(endTime)) {
      setValidationError("Slot time is invalid. Start time must be earlier than end time.");
      return;
    }
    try {
      await updateSlot({
        storeId: selectedStoreId,
        slotId: selectedSlot.slotId,
        payload: { slotName, startTime, endTime, isActive: true },
      }).unwrap();
      await replaceSlotDays({
        storeId: selectedStoreId,
        slotId: selectedSlot.slotId,
        dayOfWeeks: selectedDaysMulti.map(Number).filter((d) => d >= 0 && d <= 6),
      }).unwrap();
      await refetchSlots();
      setEditSlotModalOpen(false);
      setSuccessMessage("Slot updated.");
    } catch (error) {
      setValidationError(mapSchedulingError(error, "Failed to update slot."));
    }
  };

  const handleDeleteSlotDef = async () => {
    if (!selectedStoreId || !selectedSlot) return;
    try {
      await deleteSlot({ storeId: selectedStoreId, slotId: selectedSlot.slotId }).unwrap();
      await refetchSlots();
      await refreshAllItemSlotAssignments();
      setDirtyItemIds([]);
      setSelectedSlotId("");
      setSuccessMessage("Slot deleted.");
    } catch (error) {
      setValidationError(mapSchedulingError(error, "Failed to delete slot."));
    }
  };

  const removeAssignedItem = useCallback((itemId, assignmentsToRemove, successText) => {
    const targets = Array.isArray(assignmentsToRemove) ? assignmentsToRemove : [];
    if (!targets.length) return;
    setItemAssignmentsByItem((prev) => {
      const existing = prev[itemId] || [];
      const targetKeys = new Set(targets.map((entry) => getAssignmentKey(entry)));
      const next = existing.filter(
        (assignment) => !targetKeys.has(getAssignmentKey(assignment)),
      );
      if (next.length === existing.length) return prev;
      markDirty(itemId);
      return { ...prev, [itemId]: next };
    });
    setSuccessMessage(successText || "Item removed from selected slot.");
  }, []);

  const removeAssignedItemFromDayList = useCallback(
    async ({ itemId, slotId, dayOfWeek, itemName, slotLabel }) => {
      if (!selectedStoreId || !itemId || !slotId || Number.isNaN(Number(dayOfWeek))) {
        return;
      }

      const existing = itemAssignmentsByItem[itemId] || [];
      const filteredAssignments = existing.filter(
        (assignment) =>
          !(
            assignment?.slotId === slotId &&
            Number(assignment?.dayOfWeek) === Number(dayOfWeek)
          ),
      );

      if (filteredAssignments.length === existing.length) {
        return;
      }

      try {
        await replaceItemSlotDays({
          storeId: selectedStoreId,
          itemId,
          assignments: filteredAssignments,
        }).unwrap();

        setItemAssignmentsByItem((prev) => ({
          ...prev,
          [itemId]: filteredAssignments,
        }));
        setSavedAssignmentsByItem((prev) => ({
          ...prev,
          [itemId]: filteredAssignments,
        }));
        setDirtyItemIds((prev) => prev.filter((id) => id !== itemId));
        setLastAssignmentsSyncAt(Date.now());

        const refreshedRows = await fetchItemSlotDaysBySlot({
          storeId: selectedStoreId,
          slotId,
          dayOfWeek: Number(dayOfWeek),
        }).unwrap();
        setDayAssignmentsBySlot((prev) => ({
          ...prev,
          [slotId]: Array.isArray(refreshedRows) ? refreshedRows : [],
        }));

        setSuccessMessage(
          `${itemName || "Item"} removed from ${slotLabel || "selected slot"} for the selected day.`,
        );
      } catch (error) {
        setValidationError(
          mapSchedulingError(error, "Failed to remove item from day assignment list."),
        );
      }
    },
    [
      selectedStoreId,
      itemAssignmentsByItem,
      replaceItemSlotDays,
      fetchItemSlotDaysBySlot,
    ],
  );

  const pendingAssignedItemsForSelection = useMemo(() => {
    if (!selectedSlotId) return [];
    const selectedDaySet = new Set(selectedDaysMulti.map(Number));
    return Object.entries(itemAssignmentsByItem)
      .filter(([itemId, assignments]) => {
        const localMatches = (assignments || []).filter(
          (entry) =>
            entry.slotId === selectedSlotId &&
            selectedDaySet.has(Number(entry.dayOfWeek)),
        );
        if (localMatches.length === 0) return false;
        const saved = savedAssignmentsByItem[itemId] || [];
        return localMatches.some((entry) => !hasAssignment(saved, entry));
      })
      .map(([itemId]) => {
        const item = items.find((x) => getItemId(x) === itemId);
        return { id: itemId, name: getItemName(item || { name: itemId }) };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [
    selectedSlotId,
    selectedDaysMulti,
    itemAssignmentsByItem,
    savedAssignmentsByItem,
    items,
  ]);

  const pendingItemIds = useMemo(() => [...new Set(dirtyItemIds)], [dirtyItemIds]);
  const hasUnsavedChanges = pendingItemIds.length > 0;
  const isDataPotentiallyStale =
    Boolean(lastAssignmentsSyncAt) &&
    Date.now() - lastAssignmentsSyncAt > 2 * 60 * 1000;

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!hasUnsavedChanges) return;
        event.preventDefault();
        event.returnValue = "";
      },
      [hasUnsavedChanges],
    ),
  );

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (!hasUnsavedChanges) return;
      const link = event.target?.closest?.("a[href]");
      if (!link) return;
      if (!window.confirm("You have unsaved scheduling changes. Leave this page without saving?")) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener("click", onDocumentClick, true);
    return () => document.removeEventListener("click", onDocumentClick, true);
  }, [hasUnsavedChanges]);
  const saveAll = async () => {
    if (!selectedStoreId) return;
    if (!pendingItemIds.length) {
      setValidationError("No unsaved scheduling changes.");
      return;
    }
    setValidationError("");
    setSaveProgressSummary({
      total: pendingItemIds.length,
      completed: 0,
      success: 0,
      failed: 0,
    });
    setSaveProgressModalOpen(true);

    let completedCount = 0;
    let successCount = 0;
    let failedCount = 0;
    const failedItems = [];
    const markRow = (status) => {
      if (status === "success" || status === "failed") {
        completedCount += 1;
        if (status === "success") successCount += 1;
        if (status === "failed") failedCount += 1;
        setSaveProgressSummary({
          total: pendingItemIds.length,
          completed: completedCount,
          success: successCount,
          failed: failedCount,
        });
      }
    };

    try {
      for (const itemId of pendingItemIds) {
        markRow("saving");
        // eslint-disable-next-line no-await-in-loop
        const latestRows = await fetchItemSlotDays({
          storeId: selectedStoreId,
          itemId,
        }).unwrap();
        const latestAssignments = normalizeAssignments(latestRows);
        const baselineAssignments = savedAssignmentsByItem[itemId] || [];
        const rawAssignments = itemAssignmentsByItem[itemId] || [];
        const baselineKeys = new Set(baselineAssignments.map((entry) => getAssignmentKey(entry)));
        const localKeys = new Set(rawAssignments.map((entry) => getAssignmentKey(entry)));
        const latestKeys = new Set(latestAssignments.map((entry) => getAssignmentKey(entry)));
        const removedByUser = [...baselineKeys].filter((key) => !localKeys.has(key));
        const addedByUser = [...localKeys].filter((key) => !baselineKeys.has(key));

        const latestByKey = new Map(
          latestAssignments.map((entry) => [getAssignmentKey(entry), entry]),
        );
        const localByKey = new Map(rawAssignments.map((entry) => [getAssignmentKey(entry), entry]));

        const mergedAssignments = [...latestByKey.values()].filter(
          (entry) => !removedByUser.includes(getAssignmentKey(entry)),
        );
        addedByUser.forEach((key) => {
          const localEntry = localByKey.get(key);
          if (localEntry && !mergedAssignments.some((entry) => getAssignmentKey(entry) === key)) {
            mergedAssignments.push(localEntry);
          }
        });

        const staleDetected = [...latestKeys].some((key) => !baselineKeys.has(key));
        if (staleDetected) {
          pushToast(
            "error",
            "Latest scheduling data changed on server. Saving with merged conflict-safe updates.",
          );
        }

        const filteredAssignments = mergedAssignments.filter((entry) => {
          const dayOfWeek = Number(entry?.dayOfWeek);
          const allowedDaysForSlot = new Set(
            (slotDaysBySlotId[entry?.slotId] || [])
              .map(Number)
              .filter((d) => d >= 0 && d <= 6),
          );
          return (
            validSlotIdSet.has(entry?.slotId) &&
            !Number.isNaN(dayOfWeek) &&
            dayOfWeek >= 0 &&
            dayOfWeek <= 6 &&
            allowedDaysForSlot.has(dayOfWeek)
          );
        });
        const removedInvalidCount = mergedAssignments.length - filteredAssignments.length;

        if (removedInvalidCount > 0) {
          setItemAssignmentsByItem((prev) => ({
            ...prev,
            [itemId]: filteredAssignments,
          }));
        }

        try {
          // eslint-disable-next-line no-await-in-loop
          await replaceItemSlotDays({
            storeId: selectedStoreId,
            itemId,
            assignments: filteredAssignments,
          }).unwrap();
          markRow("success");
        } catch (_error) {
          failedItems.push(itemId);
          markRow("failed");
        }
      }
      setSavedAssignmentsByItem((prev) => {
        const next = { ...prev };
        pendingItemIds.forEach((itemId) => {
          next[itemId] = [...(itemAssignmentsByItem[itemId] || [])].filter(
            (entry) => validSlotIdSet.has(entry?.slotId),
          );
        });
        return next;
      });
      setDirtyItemIds((prev) => prev.filter((itemId) => failedItems.includes(itemId)));
      setLastAssignmentsSyncAt(Date.now());
      if (failedItems.length === 0) {
        setSuccessMessage(`Saved scheduling changes for ${pendingItemIds.length} item(s).`);
      } else {
        setValidationError(
          `Saved ${pendingItemIds.length - failedItems.length} item(s). ${failedItems.length} failed.`,
        );
      }
    } catch (error) {
      setValidationError(mapSchedulingError(error, "Failed to save item slot assignments."));
    } finally {
      setTimeout(() => {
        setSaveProgressModalOpen(false);
      }, 500);
    }
  };

  const dayAssignmentRows = useMemo(() => {
    if (reportDaySlots.length === 0) return [];

    const rows = [];
    reportDaySlots.forEach((slot) => {
      const slotLabel = normalizeSlotName(slot.slotName)
        ? slot.slotName
        : `${slot.startTime} - ${slot.endTime}`;
      const slotTime = `${slot.startTime} - ${slot.endTime}`;
      const rowsData = Array.isArray(dayAssignmentsBySlot[slot.slotId])
        ? dayAssignmentsBySlot[slot.slotId]
        : [];

      if (rowsData.length === 0) {
        rows.push({
          id: `unassigned-${slot.slotId}-${reportDayNumber}`,
          item: "Unassigned",
          slot: slotLabel,
          time: slotTime,
          actions: <span className="text-xs text-slate-500">-</span>,
        });
        return;
      }

      rowsData.forEach((entry, index) => {
        const itemId = String(entry?.itemId || entry?.id || "");
        const itemFromCatalog = items.find((item) => getItemId(item) === itemId);
        const itemName =
          entry?.itemName ||
          entry?.name ||
          getItemName(itemFromCatalog || { name: itemId || `Item ${index + 1}` });

        rows.push({
          id: `${itemId || "item"}-${slot.slotId}-${reportDayNumber}-${index}`,
          item: itemName,
          slot: slotLabel,
          time: slotTime,
          actions: (
            <ActionButton
              label={`Remove ${itemName} from ${slotLabel}`}
              icon="delete"
              tone="danger"
              onClick={() =>
                removeAssignedItemFromDayList({
                  itemId,
                  slotId: slot.slotId,
                  dayOfWeek: reportDayNumber,
                  itemName,
                  slotLabel,
                })
              }
            />
          ),
        });
      });
    });
    return rows;
  }, [
    reportDaySlots,
    dayAssignmentsBySlot,
    items,
    reportDayNumber,
    removeAssignedItemFromDayList,
  ]);

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader title="Weekly Scheduling" subtitle="Assign items to selected day and slot." />
        <Card>
          <Alert tone="warning">Please select a store to manage weekly scheduling.</Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Weekly Scheduling"
        subtitle="Manage slots centrally and assign items by slot."
        actions={
          <Button onClick={saveAll} disabled={replaceItemSlotsState.isLoading} size="sm">
            {replaceItemSlotsState.isLoading
              ? "Saving..."
              : `Save Changes (${pendingItemIds.length})`}
          </Button>
        }
      />
      {isDataPotentiallyStale && (
        <Alert tone="info">
          Assignment data may be stale. Saving will refetch latest assignments and merge your changes.
        </Alert>
      )}

      <Card className="p-4 md:p-5">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_1px_minmax(0,3fr)] xl:gap-5">
          <div className="space-y-4">
            <Input
              label="Search Items"
              value={itemSearch}
              onChange={(event) => setItemSearch(event.target.value)}
              placeholder="Search by item or category"
            />
            <div className="overflow-x-auto pb-1">
              <div className="flex min-w-max gap-2">
                {groupedItems.map((group) => (
                  <button
                    key={group.categoryId}
                    type="button"
                    onClick={() => setSelectedCategoryId(group.categoryId)}
                    className={[
                      "rounded-full border px-3 py-1.5 text-xs font-semibold transition",
                      selectedCategoryId === group.categoryId
                        ? "border-indigo-300 bg-indigo-50 text-indigo-900"
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
                    ].join(" ")}
                  >
                    {group.categoryName} ({group.items.length})
                  </button>
                ))}
              </div>
            </div>
            <div className="max-h-[560px] space-y-3 overflow-y-auto pr-1">
              {itemsLoading ? (
                <p className="text-sm text-slate-500">Loading items...</p>
              ) : groupedItems.length === 0 ? (
                <p className="text-sm text-slate-500">No items found.</p>
              ) : (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="mb-3">
                    <Checkbox
                      label="Select All in Category"
                      checked={allCategoryItemsSelected}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        const categoryItemIds = selectedCategoryItems.map((item) => getItemId(item));
                        setSelectedItemIds((prev) =>
                          checked
                            ? [...new Set([...prev, ...categoryItemIds])]
                            : prev.filter((id) => !categoryItemIds.includes(id)),
                        );
                        if (checked) {
                          if (!selectedSlotId) {
                            setValidationError("Please select a slot first.");
                            return;
                          }
                          assignItemsToSelection(categoryItemIds);
                        }
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    {selectedCategoryItems.map((item) => {
                      const itemId = getItemId(item);
                      const isActiveItem = itemId === activeItemId;
                      const isChecked = selectedItemIds.includes(itemId);
                      return (
                        <div
                          key={itemId}
                          className={[
                            "flex items-center gap-2 rounded-md border px-2 py-2 transition",
                            isActiveItem
                              ? "border-indigo-300 bg-indigo-50"
                              : "border-slate-200 bg-white",
                          ].join(" ")}
                        >
                          <Checkbox
                            label=""
                            checked={isChecked}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setSelectedItemIds((prev) =>
                                checked
                                  ? prev.includes(itemId)
                                    ? prev
                                    : [...prev, itemId]
                                  : prev.filter((id) => id !== itemId),
                              );
                              if (checked) {
                                if (!selectedSlotId) {
                                  setValidationError("Please select a slot first.");
                                  return;
                                }
                                assignItemsToSelection([itemId]);
                              }
                            }}
                          />
                          <button
                            type="button"
                            draggable
                            onDragStart={(event) => event.dataTransfer.setData("text/plain", itemId)}
                            onClick={() => setActiveItemId(itemId)}
                            className="flex-1 text-left"
                          >
                            <div className="text-sm font-semibold text-slate-800">{getItemName(item)}</div>
                            <div className="text-xs text-slate-500">Drag into right section</div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="hidden rounded-full bg-slate-200 xl:block" />

          <div className="space-y-5" onDrop={handleDrop} onDragOver={handleDragOver}>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="space-y-4">
              <div ref={dayDropdownRef} className="relative">
                <label className="mb-2 block text-sm font-medium text-slate-700">Select Day</label>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="inline-flex h-11 items-center rounded-lg border border-slate-200 bg-slate-50 p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setDaySelectionMode("single");
                      setSelectedDaysMulti([selectedDay]);
                    }}
                    className={[
                      "h-9 rounded-md px-3 text-sm font-semibold transition",
                      daySelectionMode === "single"
                        ? "bg-indigo-100 text-indigo-800"
                        : "text-slate-600 hover:bg-slate-100",
                    ].join(" ")}
                  >
                    Single day
                  </button>
                  <button
                    type="button"
                    onClick={() => setDaySelectionMode("multi")}
                    className={[
                      "h-9 rounded-md px-3 text-sm font-semibold transition",
                      daySelectionMode === "multi"
                        ? "bg-indigo-100 text-indigo-800"
                        : "text-slate-600 hover:bg-slate-100",
                    ].join(" ")}
                  >
                    Multiple days
                  </button>
                </div>
                
                {daySelectionMode === "single" ? (
                  <div className="min-w-[240px] flex-1">
                    <Select
                      label=""
                      value={selectedDay}
                      onChange={(event) => {
                        const value = event.target.value;
                        const nextDays = [value];
                        setSelectedDay(value);
                        setSelectedDaysMulti(nextDays);
                        if (
                          selectedSlotId &&
                          selectedSlotAllowedDays.size > 0 &&
                          !nextDays.every((day) => selectedSlotAllowedDays.has(Number(day)))
                        ) {
                          setSelectedSlotId("");
                          setValidationError(
                            "Selected slot does not apply to the new day selection. Please reselect a slot.",
                          );
                        }
                      }}
                    >
                      {DAYS.map((day) => (
                        <option key={day.dayOfWeek} value={String(day.dayOfWeek)}>
                          {day.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="flex h-11 min-w-[240px] flex-1 items-center justify-between rounded-lg border border-slate-200 bg-white px-4 text-left text-base text-slate-900 shadow-sm"
                    onClick={() => setDayDropdownOpen((prev) => !prev)}
                  >
                    <span>{selectedDayLabel}</span>
                    <span className="text-slate-400">v</span>
                  </button>
                )}
                </div>
                {daySelectionMode === "multi" && dayDropdownOpen && (
                  <div className="absolute left-0 top-[calc(100%+8px)] z-20 w-[320px] rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
                    <div className="space-y-1">
                      {DAYS.map((day) => {
                        const value = String(day.dayOfWeek);
                        const checked = selectedDaysMulti.includes(value);
                        return (
                          <label
                            key={day.dayOfWeek}
                            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            {daySelectionMode === "multi" && (
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(event) => {
                                  const isChecked = event.target.checked;
                                  setSelectedDaysMulti((prev) => {
                                    if (isChecked) {
                                      setSelectedDay(value);
                                      return prev.includes(value) ? prev : [...prev, value];
                                    }
                                    const next = prev.filter((d) => d !== value);
                                    return next.length === 0 ? [selectedDay] : next;
                                  });
                                }}
                                className="h-3.5 w-3.5 rounded border-slate-300 text-[#4f46e5] focus:ring-[#4f46e5]"
                              />
                            )}
                            <button
                              type="button"
                              className="flex-1 text-left"
                              onClick={() => {
                                let nextDays = [];
                                if (daySelectionMode === "single") {
                                  nextDays = [value];
                                  setSelectedDay(value);
                                  setDayDropdownOpen(false);
                                } else {
                                  nextDays = checked
                                    ? selectedDaysMulti.filter((d) => d !== value)
                                    : [...selectedDaysMulti, value];
                                  if (nextDays.length === 0) {
                                    nextDays = [selectedDay];
                                  }
                                  setSelectedDaysMulti(nextDays);
                                  if (!checked) {
                                    setSelectedDay(value);
                                  }
                                }
                                if (
                                  selectedSlotId &&
                                  selectedSlotAllowedDays.size > 0 &&
                                  !nextDays.every((day) => selectedSlotAllowedDays.has(Number(day)))
                                ) {
                                  setSelectedSlotId("");
                                  setValidationError(
                                    "Selected slot does not apply to the new day selection. Please reselect a slot.",
                                  );
                                }
                              }}
                            >
                              {day.label}
                            </button>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Slot</label>
                <div className="flex flex-wrap items-center gap-3">
                  {slotOptions.length > 0 ? (
                    <div className="min-w-[240px] flex-1 max-w-[520px]">
                      <Select label="" value={selectedSlotId} onChange={(e) => setSelectedSlotId(e.target.value)}>
                      <option value="">Select slot</option>
                      {slotOptions.map((slot) => (
                        <option key={slot.slotId} value={slot.slotId}>
                          {normalizeSlotName(slot.slotName)
                            ? `${slot.slotName} (${slot.startTime} - ${slot.endTime})`
                            : `${slot.startTime} - ${slot.endTime}`}
                        </option>
                      ))}
                      </Select>
                    </div>
                  ) : (
                    <div className="flex h-11 min-w-[240px] flex-1 items-center rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 text-sm text-slate-500 whitespace-nowrap overflow-hidden">
                      No slots available for this day.
                    </div>
                  )}
                  <div className="flex h-11 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2">
                    <ActionButton
                      label="Create Slot"
                      icon="create"
                      tone="neutral"
                      className="h-9 w-9 p-0"
                      onClick={() => {
                        setSlotForm({ slotName: "", startTime: "", endTime: "" });
                        setCreateSlotModalOpen(true);
                      }}
                    />
                    <ActionButton
                      label="Edit Slot"
                      icon="edit"
                      tone="neutral"
                      className="h-9 w-9 p-0"
                      onClick={openEditSlot}
                      disabled={!selectedSlotId}
                    />
                    <ActionButton
                      label="Delete Slot"
                      icon="delete"
                      tone="danger"
                      className="h-9 w-9 p-0"
                      onClick={handleDeleteSlotDef}
                      disabled={!selectedSlotId || deleteSlotState.isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
            </div>

            <div className="mt-3 text-sm text-slate-700">
              <span className="font-semibold">{selectedDaysMulti.length > 1 ? "Days" : "Day"}:</span>{" "}
              {selectedDayNamesText || "-"} | <span className="font-semibold">Slot:</span>{" "}
              {selectedSlot
                ? normalizeSlotName(selectedSlot.slotName)
                  ? `${selectedSlot.slotName} (${selectedSlot.startTime} - ${selectedSlot.endTime})`
                  : `${selectedSlot.startTime} - ${selectedSlot.endTime}`
                : "-"}
            </div>
            <div className="mt-3">
              <p className="mb-2 text-sm font-semibold text-slate-700">
                Pending (Not Saved Yet)
              </p>
              {pendingAssignedItemsForSelection.length === 0 ? (
                <p className="text-sm text-slate-500">No pending assignments.</p>
              ) : (
                <div className="space-y-2">
                  {pendingAssignedItemsForSelection.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                    >
                      <span className="text-sm font-semibold text-amber-900">
                        {item.name}
                      </span>
                      <ActionButton
                        label={`Remove ${item.name}`}
                        icon="delete"
                        tone="danger"
                        onClick={() =>
                          removeAssignedItem(
                            item.id,
                            selectedDaysMulti
                              .map(Number)
                              .filter((dayOfWeek) => dayOfWeek >= 0 && dayOfWeek <= 6)
                              .map((dayOfWeek) => ({ slotId: selectedSlotId, dayOfWeek })),
                            "Item removed from pending assignment.",
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">
              Day Assignment List
            </h3>
            <p className="text-xs text-slate-500">
              View all slots and assigned items for selected day.
            </p>
          </div>
          <div className="w-[220px]">
            <Select
              label="Select Day"
              value={reportDay}
              onChange={(event) => setReportDay(event.target.value)}
            >
              {DAYS.map((day) => (
                <option key={day.dayOfWeek} value={String(day.dayOfWeek)}>
                  {day.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
        {dayAssignmentsLoading && (
          <p className="mb-3 text-sm text-slate-500">Loading assignments...</p>
        )}
        {!dayAssignmentsLoading && reportDaySlots.length === 0 && (
          <p className="mb-3 text-sm text-slate-500">No slots mapped for selected day.</p>
        )}
        <Table
          columns={[
            { key: "item", label: "Item" },
            { key: "slot", label: "Slot" },
            { key: "time", label: "Time" },
            { key: "actions", label: "Actions" },
          ]}
          rows={dayAssignmentRows}
          emptyText="No assignments found for selected day."
        />
      </Card>

      <style>
        {`@keyframes weeklyToastIn{from{transform:translateX(100%);opacity:0;}to{transform:translateX(0);opacity:1;}}`}
      </style>
      <div className="pointer-events-none fixed right-4 top-4 z-[100] flex w-full max-w-sm flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            style={{ animation: "weeklyToastIn 240ms ease-out" }}
            className={[
              "pointer-events-auto rounded-lg border px-4 py-3 shadow-lg",
              toast.tone === "error"
                ? "border-rose-200 bg-rose-50 text-rose-800"
                : "border-emerald-200 bg-emerald-50 text-emerald-800",
            ].join(" ")}
          >
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-medium">{toast.message}</p>
              <button
                type="button"
                onClick={() =>
                  setToasts((prev) => prev.filter((entry) => entry.id !== toast.id))
                }
                className="text-xs font-semibold opacity-70 transition hover:opacity-100"
              >
                Close
              </button>
            </div>
          </div>
        ))}
      </div>

      <Modal
        isOpen={saveProgressModalOpen}
        onClose={() => {}}
        title="Saving Item Assignments"
        size="md"
        hideCloseButton
        backdropClassName="bg-black/75 backdrop-blur-md"
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
            <div className="flex items-center gap-3">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
              <p className="text-sm font-semibold text-slate-800">Adding items...</p>
            </div>
            <p className="mt-2 text-xs text-slate-600">
              Saved {saveProgressSummary.completed} of {saveProgressSummary.total}
            </p>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full bg-indigo-500 transition-all duration-300"
                style={{
                  width: `${
                    saveProgressSummary.total > 0
                      ? (saveProgressSummary.completed / saveProgressSummary.total) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>

          {saveProgressSummary.failed > 0 && (
            <Alert tone="warning">
              Some items failed to save. Please review statuses above.
            </Alert>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={createSlotModalOpen}
        onClose={() => setCreateSlotModalOpen(false)}
        title="Create Slot"
        onConfirm={handleCreateSlot}
        confirmText="Save"
      >
        <div className="grid gap-4">
          <Input
            label="Slot Name"
            placeholder="Breakfast Slot"
            value={slotForm.slotName}
            onChange={(event) => setSlotForm((prev) => ({ ...prev, slotName: event.target.value }))}
          />
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Start Time"
              type="time"
              value={slotForm.startTime}
              onChange={(event) => setSlotForm((prev) => ({ ...prev, startTime: event.target.value }))}
            />
            <Input
              label="End Time"
              type="time"
              value={slotForm.endTime}
              onChange={(event) => setSlotForm((prev) => ({ ...prev, endTime: event.target.value }))}
            />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={editSlotModalOpen}
        onClose={() => setEditSlotModalOpen(false)}
        title="Edit Slot"
        onConfirm={handleEditSlot}
        confirmText="Update"
      >
        <div className="grid gap-4">
          <Input
            label="Slot Name"
            value={slotForm.slotName}
            onChange={(event) => setSlotForm((prev) => ({ ...prev, slotName: event.target.value }))}
          />
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Start Time"
              type="time"
              value={slotForm.startTime}
              onChange={(event) => setSlotForm((prev) => ({ ...prev, startTime: event.target.value }))}
            />
            <Input
              label="End Time"
              type="time"
              value={slotForm.endTime}
              onChange={(event) => setSlotForm((prev) => ({ ...prev, endTime: event.target.value }))}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WeeklyScheduling;
